"use client";

import React, { useState, useEffect } from "react";
import TrackedLink from "@/components/custom/analytics/TrackedLink";
import { getBlogList, getBlogCalendar } from "@/lib/api/client/blog";
import { useTranslations, useLocale } from "next-intl";
import { Calendar } from "@/components/ui/calendar";
import Image from "next/image";
import { endOfToday, format, isAfter, parseISO, type Locale } from "date-fns";
import { enUS, zhCN, ja, es, ko, pt, de, fr, it, zhTW } from "date-fns/locale";
import Pagination from "@/components/custom/Pagination";
import { useIntlRouter } from "@/lib/utils/navigation";
import { MixpanelEvent } from "@/constant/mixpanelEvent";

interface BlogItem {
  title: string;
  description: string;
  date: string;
  image: string;
  href: string;
  blogId?: string;
  author?: string;
  status?: string;
  publishTimestamp?: number;
}

interface BlogByDateListProps {
  slug: string;
  initialBlogList: BlogItem[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export default function BlogByDateList(props: BlogByDateListProps) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useIntlRouter();
  const {
    slug,
    initialBlogList,
    totalPages: initialTotalPages,
    currentPage = 1,
    pageSize,
  } = props;

  // 语言映射：将 next-intl 的 locale 映射到 date-fns 的 locale
  const localeMap: Record<string, Locale> = {
    en: enUS,
    zh: zhCN,
    "zh-Hant": zhTW,
    ja: ja,
    es: es,
    ko: ko,
    pt: pt,
    de: de,
    fr: fr,
    it: it,
  };

  const dateFnsLocale = localeMap[locale] || enUS;

  // ex: 从 slug 解析日期：'2025-10-26' 格式
  const dateParts = slug.split("-");
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);

  // 初始化状态
  const [blogList, setBlogList] = useState<BlogItem[]>(initialBlogList);
  const [page, setPage] = useState(currentPage);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [loading, setLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(parseISO(slug));
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    parseISO(slug)
  );

  // 获取日历数据
  const fetchCalendar = async (year: number, month: number) => {
    try {
      const calendarResponse = await getBlogCalendar({
        year,
        month,
      });

      if (calendarResponse?.metadata?.date_list) {
        setAvailableDates(calendarResponse.metadata.date_list);
      }
    } catch (error) {
      console.error("Error fetching blog calendar:", error);
    }
  };

  // 初始化时获取当前月份的日历数据
  useEffect(() => {
    fetchCalendar(year, month);
  }, [year, month]);

  // 根据日期查询博客列表
  const fetchBlogList = async (
    pageNum: number,
    selectedYear: number,
    selectedMonth: number,
    selectedDay: number
  ) => {
    setLoading(true);
    try {
      const blogListResponse = await getBlogList({
        page: pageNum,
        page_size: pageSize,
        year: selectedYear,
        month: selectedMonth,
        day: selectedDay,
      });

      if (blogListResponse?.metadata?.blog_list) {
        const blogs = blogListResponse.metadata.blog_list.map((item) => {
          return {
            title: item.content?.title ?? "",
            description: item.content?.description ?? "",
            date: item.publish_date,
            image: item.cover_url,
            href: `/blog/${item.url_blog_name}`,
          };
        });
        setBlogList(blogs);
        setPage(pageNum);
        setTotalPages(
          Math.ceil((blogListResponse.metadata.total || 0) / pageSize)
        );
      }
    } catch (error) {
      console.error("Error fetching blog list from API:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = async (date?: Date) => {
    if (!date) return;

    setSelectedDate(date);
    const selectedYear = date.getFullYear();
    const selectedMonth = date.getMonth() + 1;
    const selectedDay = date.getDate();

    // 更新 URL
    const dateStr = format(date, "yyyy-MM-dd");
    router.push(`/blog/date/${dateStr}`);

    // 查询选中日期的博客列表
    await fetchBlogList(1, selectedYear, selectedMonth, selectedDay);
  };

  const handleMonthChange = async (month: Date) => {
    setCurrentMonth(month);
    const year = month.getFullYear();
    const monthNum = month.getMonth() + 1;

    // 获取新月份的日历数据
    await fetchCalendar(year, monthNum);
  };

  const loadPage = async (pageNum: number) => {
    if (!selectedDate) return;

    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth() + 1;
    const selectedDay = selectedDate.getDate();

    await fetchBlogList(pageNum, selectedYear, selectedMonth, selectedDay);
  };

  return (
    <div className="min-h-[740px]">
      <div className="flex flex-col-reverse gap-[32px] lg:flex-row">
        <div className="lg:flex-1">
          {blogList.length === 0 ? (
            <div className="flex flex-col items-center justify-center mt-[120px] mb-[160px]">
              <Image
                src="/assets/image/share/share-invalid.webp"
                alt="No related blog available"
                width={132}
                height={132}
                sizes="132px"
                className="my-0 w-[132px] rounded-none"
              />
              <p className="text-[16px] md:text-[20px] text-[#ABAAA6] font-serif">
                {t("blog.noRelatedBlogAvailable")}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-y-[32px]">
                {blogList.map((blog) => {
                  // 从href中提取博客ID，优先使用blogId字段
                  const blogId =
                    blog.blogId ||
                    blog.href.replace("/blog/", "").replace("/", "");

                  return (
                    <TrackedLink
                      href={blog.href}
                      key={blogId}
                      className="group gap-[24px]"
                      mixpanelEvent={MixpanelEvent.ClickBlog}
                      mixpanelEventData={{ blog_id: blogId }}
                      useIntlLink={true}
                    >
                      <div className="flex gap-[24px] items-center">
                        <div className="md:w-[240px] md:h-[120px] w-[140px] h-[100px] overflow-hidden rounded-[12px] flex-shrink-0">
                          <Image
                            src={blog.image}
                            alt={blog.title}
                            width={480}
                            height={240}
                            quality={80}
                            sizes="(min-width: 768px) 240px, 140px"
                            className="my-0 h-full w-full rounded-none object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>
                        <div className="flex flex-col gap-[12px]">
                          {/* 超过两行显示 ... */}
                          <h3
                            className="md:text-[24px] text-[16px] font-semibold text-[#161615] transition-colors"
                            style={{
                              lineHeight: "normal",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {blog.title}
                          </h3>
                          <div className="md:text-[16px] text-[14px] text-[#ABAAA6]">
                            {blog.date}
                          </div>
                        </div>
                      </div>
                    </TrackedLink>
                  );
                })}
              </div>
              {/* Pagination */}
              {totalPages <= 1 ? (
                <div className="flex justify-center items-center mt-[32px] md:mb-[72px] sm:mb-[0px]">
                  <div className="flex-1 max-w-[120px] h-[1px] bg-[#DAD8D3] rounded-full"></div>
                  <div className="px-[24px] flex-shrink-0">
                    <p className="text-[#ABAAA6] text-[16px] font-[400] whitespace-nowrap">
                      {t("blog.noMorePosts")}
                    </p>
                  </div>
                  <div className="flex-1 max-w-[120px] h-[1px] bg-[#DAD8D3] rounded-full"></div>
                </div>
              ) : (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={loadPage}
                  loading={loading}
                />
              )}
            </>
          )}
        </div>

        {/* 日期控件 */}
        <aside className="lg:w-[260px] w-full lg:sticky lg:top-[120px]">
          <div className="rounded-[16px] bg-[#F1EFE9] px-[12px] py-[12px]">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              month={currentMonth}
              onMonthChange={handleMonthChange}
              disabled={[
                (date) => isAfter(date, endOfToday()),
                (date) => !availableDates.includes(format(date, "yyyy-MM-dd")),
              ]}
              className="p-0"
              formatters={{
                formatCaption: (date: Date) =>
                  format(date, "MMM yyyy", { locale: dateFnsLocale }),
                formatWeekdayName: (date: Date) =>
                  format(date, "EEE", { locale: dateFnsLocale }),
              }}
              classNames={{
                months: "flex flex-col space-y-[12px]",
                month: "space-y-[12px]",
                caption: "flex items-center justify-center relative mb-[12px]",
                caption_label: "text-[#161615] text-[18px] font-serif",
                nav: "flex items-center justify-between absolute inset-x-0",
                nav_button:
                  "w-auto h-auto bg-transparent border-0 hover:bg-transparent focus-visible:ring-0",
                nav_button_previous: "flex items-center justify-center",
                nav_button_next: "flex items-center justify-center",
                table: "w-full border-collapse",
                head_row: "flex",
                head_cell:
                  "flex-1 text-center text-[12px] font-medium text-[#B3B1AC]",
                row: "flex w-full",
                cell: "flex-1 h-[40px] flex items-center justify-center",
                day: "h-[25px] w-[25px] rounded-full flex items-center justify-center text-[14px] text-[#2A2A29] hover:bg-[#FFF1F3] focus:bg-[#FFF1F3] aria-selected:text-white",
                day_selected:
                  "text-white hover:text-white focus:text-white [background:linear-gradient(98deg,#FFC300_0.21%,#FF5A70_50.21%,#F63B3B_100.21%)] hover:[background:linear-gradient(98deg,#FFC300_0.21%,#FF5A70_50.21%,#F63B3B_100.21%)] focus:[background:linear-gradient(98deg,#FFC300_0.21%,#FF5A70_50.21%,#F63B3B_100.21%)]",
                day_disabled:
                  "text-[#D0CDC7] pointer-events-none opacity-60 hover:bg-transparent !text-[#D0CDC7]",
                day_outside: "text-[#D0CDC7] opacity-60",
              }}
              components={{
                IconLeft: ({ ...props }) => (
                  <img
                    src="/assets/image/blog/prev-arrow.svg"
                    alt="prev"
                    className="w-[7px] h-[10px]"
                    {...props}
                  />
                ),
                IconRight: ({ ...props }) => (
                  <img
                    src="/assets/image/blog/next-arrow.svg"
                    alt="next"
                    className="w-[7px] h-[10px]"
                    {...props}
                  />
                ),
              }}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
